// src/web/routes/_authenticated/browse/$assetId.tsx
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertCircleIcon, ArrowLeftIcon, RefreshCwIcon } from 'lucide-react'
import { useEffect } from 'react'
import { AssetDetail } from '@/components/features/AssetDetail'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAsset } from '@/lib/hooks/useAsset'
import { useMarkAssetNotificationsRead } from '@/lib/hooks/useNotifications'

export const Route = createFileRoute('/_authenticated/browse/$assetId')({
	component: AssetDetailPage,
	// ?comment=<id> deep link (mention emails): opens the Comments tab,
	// highlights the comment, and seeks to its timestamp/page
	validateSearch: (search: Record<string, unknown>): { comment?: string } =>
		typeof search.comment === 'string' && search.comment ? { comment: search.comment } : {},
})

function AssetDetailPage() {
	const { assetId } = Route.useParams()
	const { comment: highlightCommentId } = Route.useSearch()
	const { data: asset, isLoading, error, refetch } = useAsset(assetId)

	// Opening an asset clears its notifications — this is the auto-read path for
	// arriving via a mention email or the activity feed.
	const markAssetRead = useMarkAssetNotificationsRead()
	const markAssetReadFn = markAssetRead.mutate
	useEffect(() => {
		markAssetReadFn(assetId)
	}, [assetId, markAssetReadFn])

	// Error state
	if (error) {
		return (
			<div className="h-full flex items-center justify-center px-8 sm:px-14 pt-20">
				<div className="glass-card max-w-md text-center">
					<AlertCircleIcon className="h-12 w-12 text-destructive mx-auto mb-4" />
					<h2 className="text-xl font-semibold mb-2">Failed to Load Asset</h2>
					<p className="text-muted-foreground mb-6">
						{error.message || 'An error occurred while loading the asset.'}
					</p>
					<div className="flex justify-center gap-3">
						<Button variant="outline" asChild>
							<Link to="/browse">
								<ArrowLeftIcon className="h-4 w-4 mr-2" />
								Back to Browse
							</Link>
						</Button>
						<Button onClick={() => refetch()}>
							<RefreshCwIcon className="h-4 w-4 mr-2" />
							Try Again
						</Button>
					</div>
				</div>
			</div>
		)
	}

	// Loading state
	if (isLoading || !asset) {
		return (
			<div className="h-full flex flex-col px-8 sm:px-14 pt-20">
				{/* Header skeleton */}
				<div className="flex items-center justify-between py-4 border-b">
					<div className="flex items-center gap-4">
						<Skeleton className="h-9 w-9" />
						<div>
							<Skeleton className="h-6 w-48 mb-1" />
							<Skeleton className="h-4 w-24" />
						</div>
					</div>
					<div className="flex gap-2">
						<Skeleton className="h-9 w-24" />
						<Skeleton className="h-9 w-28" />
					</div>
				</div>
				{/* Content skeleton */}
				<div className="flex-1 grid grid-cols-1 lg:grid-cols-3">
					<div className="lg:col-span-2 p-6">
						<Skeleton className="w-full aspect-video rounded-lg" />
					</div>
					<div className="p-4 border-l space-y-4">
						<Skeleton className="h-8 w-32" />
						<div className="space-y-3">
							{[1, 2, 3, 4, 5].map((i) => (
								<div key={i} className="flex items-start gap-3">
									<Skeleton className="h-4 w-4" />
									<div className="flex-1">
										<Skeleton className="h-3 w-16 mb-1" />
										<Skeleton className="h-5 w-full" />
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		)
	}

	return <AssetDetail asset={asset} highlightCommentId={highlightCommentId ?? null} />
}
